import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ShoppingBag, CheckSquare, StickyNote, Plug, ChevronRight, ChevronDown, Package, LogOut, RotateCcw, FileSpreadsheet, LayoutDashboard, BarChart3, Workflow, Inbox, Mail, Mailbox, Bell, PackagePlus, Receipt, UserCog, Menu, X, Settings as SettingsIcon, QrCode, Star, PanelLeftClose, PanelLeftOpen, Store, MessageCircle, Calculator, Plus, Printer, Wallet, Megaphone, PhoneCall, Sparkles, CalendarClock, LayoutTemplate } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Grouped sidebar nav. Each group renders as a collapsible section.
// Integrări / Echipa / Companie / Setări are NOT here — they live in the
// top bar next to the bell as quick-access icons.
const NAV_GROUPS = [
  {
    id: "ops", label: "Operațional", defaultOpen: true,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard", page: "dashboard", end: true },
      { to: "/orders", label: "Comenzi", icon: ShoppingBag, testId: "nav-orders", page: "orders", badgeKey: "orders" },
      { to: "/products", label: "Produse", icon: Package, testId: "nav-products", page: "products" },
      { to: "/marketplace-offers", label: "Oferte Marketplace", icon: Store, testId: "nav-marketplace-offers", page: "products" },
      { to: "/emag-campaigns", label: "Campanii eMAG", icon: Megaphone, testId: "nav-emag-campaigns" },
      { to: "/creatives", label: "Creative Ads", icon: Sparkles, testId: "nav-creatives" },
      { to: "/ads", label: "Meta & Google Ads", icon: Megaphone, testId: "nav-ads" },
      { to: "/landing-pages", label: "Landing pages", icon: LayoutTemplate, testId: "nav-landing-pages" },
      { to: "/social-posts", label: "Postări automate", icon: CalendarClock, testId: "nav-social-posts" },
      { to: "/imports", label: "Importuri", icon: PackagePlus, testId: "nav-imports", page: "imports" },
      { to: "/calculations", label: "Calcule & Status", icon: Calculator, testId: "nav-calculations" },
      { to: "/qr-codes", label: "Coduri QR", icon: QrCode, testId: "nav-qr-codes" },
    ],
  },
  {
    id: "comm", label: "Comunicare", defaultOpen: true,
    items: [
      { to: "/messages", label: "Mesaje", icon: Inbox, testId: "nav-messages", page: "messages", badgeKey: "messages" },
      { to: "/messenger", label: "Messenger & Instagram", icon: MessageCircle, testId: "nav-messenger", page: "messages", badgeKey: "messenger" },
      { to: "/mail", label: "Mail", icon: Mailbox, testId: "nav-mail", page: "mail", badgeKey: "mail" },
      { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, testId: "nav-whatsapp", page: "whatsapp", badgeKey: "whatsapp" },
      { to: "/email-templates", label: "Șabloane", icon: Mail, testId: "nav-email-templates", page: "email_templates" },
      { to: "/notes", label: "Notițe", icon: StickyNote, testId: "nav-notes", page: "notes" },
      { to: "/tasks", label: "Tasks", icon: CheckSquare, testId: "nav-tasks", page: "tasks" },
    ],
  },
  {
    id: "returns", label: "Retururi & Service", defaultOpen: false,
    items: [
      { to: "/returns", label: "Retururi & Garanții", icon: RotateCcw, testId: "nav-returns", page: "returns" },
      { to: "/emag-rmas", label: "Retururi eMAG", icon: PackagePlus, testId: "nav-emag-rmas", page: "emag_rmas" },
      { to: "/compensations", label: "Cereri Despăgubiri și Recepții eMAG", icon: Receipt, testId: "nav-compensations", page: "returns" },
    ],
  },
  {
    id: "automation", label: "Automatizare & Rapoarte", defaultOpen: false,
    items: [
      { to: "/automations", label: "Automatizări", icon: Workflow, testId: "nav-automations", page: "automations" },
      { to: "/reports", label: "Rapoarte", icon: BarChart3, testId: "nav-reports", page: "reports" },
    ],
  },
  {
    id: "sales", label: "Vânzări", defaultOpen: false,
    items: [
      { to: "/customers", label: "Clienți", icon: UserCog, testId: "nav-customers", page: "customers" },
      { to: "/invoices", label: "Facturi", icon: Receipt, testId: "nav-invoices", page: "invoices" },
      { to: "/financiar", label: "Financiar", icon: Wallet, testId: "nav-financiar" },
      { to: "/reviews", label: "Recenzii", icon: Star, testId: "nav-reviews" },
      { to: "/apeluri-recenzii", label: "Apeluri Recenzii", icon: PhoneCall, testId: "nav-apeluri-recenzii" },
    ],
  },
];

function useUnreadMessages(enabled) {
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/messages/count`);
        if (cancelled) return;
        // Sidebar red badge = messages needing OUR reply (initiated by
        // client/eMAG, not yet answered by us). More reliable than the
        // eMAG `needsResponse` flag we used before (user request 2026-02).
        const c = Number(r.data?.unanswered ?? r.data?.unread ?? 0);
        if (prevRef.current !== null && c > prevRef.current) {
          try {
            import("sonner").then(({ toast }) => toast.message(`Mesaj nou de răspuns (${c} în așteptare)`));
          } catch (_e) { /* sonner load may fail, ignore */ }
        }
        prevRef.current = c;
        setUnread(c);
      } catch (_e) { /* polling is best-effort */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [enabled]);
  return unread;
}

function useUnreadMail(enabled) {
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/mail/unread-count`);
        if (cancelled) return;
        const c = Number(r.data?.unread || 0);
        // Toast dezactivat la cererea userului (2026-08). Contorul din
        // clopoțel/badge rămâne activ — doar toast-ul din colț a fost scos.
        prevRef.current = c;
        setUnread(c);
      } catch (_e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [enabled]);
  return unread;
}

function useUnreadWhatsApp(enabled) {
  const [unread, setUnread] = useState(0);
  const prevRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/whatsapp/unread-count`);
        if (cancelled) return;
        const c = Number(r.data?.total || 0);
        if (prevRef.current !== null && c > prevRef.current) {
          try {
            // Sound + browser notification on new WhatsApp message
            const audio = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=");
            audio.play().catch(() => {});
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
              new Notification("Mesaj WhatsApp nou", { body: `${c} mesaj(e) necitite în WhatsApp` });
            }
            import("sonner").then(({ toast }) => toast.message(`Mesaj WhatsApp nou (${c} necitite)`));
          } catch (_e) { /* ignore */ }
        }
        prevRef.current = c;
        setUnread(c);
      } catch (_e) { /* ignore */ }
    };
    load();
    // Faster than mail because WhatsApp is "instant-feel" critical.
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [enabled]);
  return unread;
}

function useUnreadMessenger(enabled) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/messenger/unread-count`);
        if (!cancelled) setUnread(Number(r.data?.total || 0));
      } catch (_e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 15000);
    window.addEventListener("messenger-read", load);
    return () => { cancelled = true; clearInterval(iv); window.removeEventListener("messenger-read", load); };
  }, [enabled]);
  return unread;
}

function useNewOrders(enabled) {
  const [count, setCount] = useState(0);
  const prevRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/orders/new-count`);
        if (cancelled) return;
        const c = Number(r.data?.new || 0);
        // Toast dezactivat la cererea userului (2026-08). Badge din navbar
        // continuă să reflecte comenzile noi în așteptare.
        prevRef.current = c;
        setCount(c);
      } catch (_e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [enabled]);
  return count;
}

function PrintQueueBadge({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/print-queue`);
      setItems(r.data?.items || []);
    } catch (_e) { /* polling best-effort */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const printAll = async () => {
    if (!items.length || busy) return;
    setBusy(true); setMsg("Se pregătește PDF-ul...");
    // Snapshot the queue AT THE MOMENT OF CLICK. This prevents a second
    // click (or a poll that finished during the fetch) from resending items
    // the user already asked to print. Fixes the "printed twice" bug.
    const snapshot = items.slice();
    // Clear the local queue immediately so a rapid double-click or the 15s
    // poll cannot re-add the same order to a second print job.
    setItems([]);
    try {
      // 1) Cerem PDF-ul merged de la backend, în ordinea din listă (stabilă)
      const orders = snapshot.map((it) => ({ id: it.order_id, integration_key: it.integration_key }));
      const idsToMark = snapshot.map((it) => it.composite_id || it.order_id).filter(Boolean);
      // 2) Marcăm ca printate PARALEL cu descărcarea PDF-ului — nu mai
      //    lăsăm o fereastră de 800ms în care queue-ul apare încă „plin".
      const [r] = await Promise.all([
        axios.post(`${API}/orders/bulk/awb-download`, { orders }, { responseType: "blob" }),
        axios.post(`${API}/print-queue/mark-printed`, { order_ids: idsToMark }).catch(() => null),
      ]);
      // 3) Deschidem PDF-ul într-un tab nou, gata pentru Cmd+P
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        setMsg("Popup blocat — permite popup-uri pentru bragus-app.");
        setBusy(false);
        return;
      }
      setMsg(`${snapshot.length} AWB-uri trimise la print. Apasă Cmd+P (Mac) sau Ctrl+P (Win) în tab-ul deschis.`);
      setTimeout(() => { setBusy(false); setMsg(""); }, 4000);
    } catch (e) {
      setMsg("Eroare: " + (e?.response?.data?.detail || e.message));
      // Restaurăm queue-ul dacă a eșuat descărcarea, ca user-ul să încerce iar.
      setItems(snapshot);
      setBusy(false);
    }
  };

  const btnCls = variant === "mobile"
    ? "relative rounded-lg p-2 text-zinc-300 transition hover:bg-zinc-900 hover:text-cyan-300 active:scale-95"
    : "relative inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition hover:border-cyan-500/50 hover:text-cyan-200";

  const count = items.length;

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid={`print-queue-btn-${variant}`}
        onClick={() => setOpen((v) => !v)}
        title={count ? `${count} AWB-uri de printat` : "Coada de printare (goală)"}
        aria-label="Coada de printare"
        className={btnCls}
      >
        <Printer className={variant === "mobile" ? "h-5 w-5" : "h-3.5 w-3.5"} />
        {variant !== "mobile" && <span>Print</span>}
        {count > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-cyan-500 px-1 text-[9px] font-bold text-zinc-900" data-testid="print-queue-count">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 w-80 rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl" data-testid="print-queue-popover">
          <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
            <div>
              <div className="text-xs font-semibold text-cyan-200">Coada de printare AWB</div>
              <div className="text-[10px] text-zinc-500">{count} {count === 1 ? "AWB pending" : "AWB-uri pending"}</div>
            </div>
            <button
              onClick={printAll}
              disabled={!count || busy}
              data-testid="print-queue-print-all"
              className="inline-flex items-center gap-1 rounded-md bg-cyan-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-cyan-500 disabled:opacity-40"
            >
              <Printer className="h-3 w-3" /> {busy ? "..." : "Printează toate"}
            </button>
          </div>
          {msg && <div className="border-b border-zinc-800 bg-cyan-950/40 px-3 py-2 text-[11px] text-cyan-200">{msg}</div>}
          <div className="max-h-64 overflow-y-auto">
            {count === 0 && (
              <div className="p-4 text-center text-[11px] text-zinc-500">Coada e goală. Configurează o automatizare cu acțiunea &quot;Adaugă AWB în coada de printat&quot;.</div>
            )}
            {items.map((it) => (
              <div key={it.composite_id || it.order_id} className="flex items-center justify-between border-b border-zinc-900 px-3 py-1.5 text-[11px] last:border-b-0">
                <div className="flex-1 min-w-0">
                  <div className="truncate text-zinc-200">{it.customer_name || "—"}</div>
                  <div className="truncate text-zinc-500">#{it.order_id} · {it.awb_number}</div>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Scoți AWB-ul #${it.order_id} din coadă (nu se va mai printa)?`)) return;
                    try {
                      await axios.post(`${API}/print-queue/mark-printed`, { order_ids: [it.composite_id || it.order_id] });
                      setItems((prev) => prev.filter((x) => (x.composite_id || x.order_id) !== (it.composite_id || it.order_id)));
                    } catch (e) {
                      setMsg("Eroare: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid={`print-queue-remove-${it.order_id}`}
                  className="ml-2 rounded p-1 text-zinc-500 hover:bg-red-900/30 hover:text-red-400"
                  title="Scoate din coadă"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          {count > 0 && (
            <div className="border-t border-zinc-800 px-3 py-2 text-[10px] text-zinc-500">
              💡 După click pe &quot;Printează toate&quot;: se deschide un tab cu PDF-ul → Cmd+P (Mac) / Ctrl+P (Win) → imprimanta ta default (setează termica ca default în Preferences).
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function QuickTaskButton({ variant = "desktop" }) {
  // Shortcut din top bar: creează task cu prioritate high în categoria „Urgente"
  // din primul board accesibil (preferat „Personal"). Popover mic, Enter =
  // save, Esc = închide.
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErr(""); setOk("");
    setTimeout(() => inputRef.current?.focus(), 30);
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = title.trim();
    if (!t || saving) return;
    setSaving(true); setErr("");
    try {
      const r = await axios.post(`${API}/tasks/quick-urgent`, { title: t });
      setOk(`Adăugat în „${r.data?.board_name} → ${r.data?.category_name}"`);
      setTitle("");
      setTimeout(() => { setOpen(false); setOk(""); }, 1200);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Eroare");
    } finally { setSaving(false); }
  };

  const btnCls = variant === "mobile"
    ? "rounded-lg p-2 text-zinc-300 transition hover:bg-zinc-900 hover:text-orange-200 active:scale-95"
    : "inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition hover:border-rose-500/50 hover:text-rose-200";

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid={`quick-urgent-task-${variant}`}
        onClick={() => setOpen((v) => !v)}
        title="Task urgent"
        aria-label="Adaugă task urgent"
        className={btnCls}
      >
        <Plus className={variant === "mobile" ? "h-5 w-5" : "h-3.5 w-3.5"} />
        {variant !== "mobile" && <span>Urgent</span>}
      </button>
      {open && (
        <div className={`absolute right-0 top-full mt-2 z-40 w-72 rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl ${variant === "mobile" ? "-right-2" : ""}`} data-testid="quick-urgent-popover">
          <form onSubmit={submit} className="p-3">
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-rose-400">
              Task urgent
            </label>
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ce trebuie făcut acum?"
              data-testid="quick-urgent-title"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-rose-500"
            />
            {err && <p className="mt-1.5 text-[11px] text-red-400">{err}</p>}
            {ok && <p className="mt-1.5 text-[11px] text-emerald-400">{ok}</p>}
            <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
              <span>Adăugat în → <span className="text-zinc-400">Personal / Urgente</span></span>
              <button
                type="submit"
                disabled={!title.trim() || saving}
                data-testid="quick-urgent-save"
                className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-rose-500 disabled:opacity-40"
              >
                {saving ? "..." : "Salvează"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


// Compact "+ Factură" button in the top bar — shortcut to add a Financiar
// invoice from any page. Minimal set of fields (name, total, currency, due
// date, status); everything else defaults to what the full Financiar modal
// uses. Backend enforces TVA=0 for non-RON currencies.
function QuickExpenseButton({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ce, setCe] = useState("");
  const [suma, setSuma] = useState("");
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [labels, setLabels] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setTimeout(() => inputRef.current?.focus(), 30);
    // fetch autocomplete labels for "ce reprezintă"
    (async () => {
      try {
        const r = await axios.get(`${API}/financiar/cheltuieli/labels`);
        setLabels(Array.isArray(r.data?.items) ? r.data.items : []);
      } catch (_e) { /* optional */ }
    })();
    const onClick = (e) => {
      // 2026-02 fix: guard against React re-render între target-ul evenimentului
      // și rularea acestui listener (`setShowSug(false)` din suggestion click
      // demount-uia div-ul → `contains(e.target)` returna false → popover-ul
      // se închidea imediat după click pe sugestie). `composedPath()` reflectă
      // ierarhia LA MOMENTUL EVENIMENTULUI, deci vede corect wrapRef.
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      if (wrapRef.current && path.includes(wrapRef.current)) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const suggestions = (() => {
    const q = (ce || "").trim().toLowerCase();
    if (!q) return labels.slice(0, 6);
    return labels.filter((l) => (l.label || "").toLowerCase().includes(q)).slice(0, 6);
  })();

  const sumaNum = parseFloat(suma) || 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!ce.trim() || sumaNum <= 0 || saving) return;
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/financiar/cheltuieli`, {
        ce_reprezinta: ce.trim(),
        suma: Number(sumaNum.toFixed(2)),
        data,
        note: note.trim() || "",
      });
      setCe(""); setSuma(""); setNote("");
      setData(new Date().toISOString().slice(0, 10));
      setOpen(false);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Eroare");
    } finally { setSaving(false); }
  };

  const btnCls = variant === "mobile"
    ? "rounded-lg p-2 text-zinc-300 transition hover:bg-zinc-900 hover:text-orange-200 active:scale-95"
    : "inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition hover:border-orange-500/50 hover:text-orange-200";

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid={`quick-expense-${variant}`}
        onClick={() => setOpen((v) => !v)}
        title="Adaugă cheltuială (Financiar)"
        aria-label="Adaugă cheltuială"
        className={btnCls}
      >
        <Receipt className={variant === "mobile" ? "h-5 w-5" : "h-3.5 w-3.5"} />
        {variant !== "mobile" && <span>Cheltuială</span>}
      </button>
      {open && (
        <div className={`absolute right-0 top-full mt-2 z-40 w-96 rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl ${variant === "mobile" ? "-right-2" : ""}`} data-testid="quick-expense-popover">
          <form onSubmit={submit} className="space-y-2 p-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wide text-orange-400">
              Adaugă cheltuială lunară
            </label>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={ce}
                onChange={(e) => { setCe(e.target.value); setShowSug(true); }}
                onFocus={() => setShowSug(true)}
                onBlur={() => setTimeout(() => setShowSug(false), 150)}
                placeholder="Ce reprezintă (ex: Chirie, Internet, Bonus)"
                data-testid="quick-expense-desc"
                autoComplete="off"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500"
              />
              {showSug && suggestions.length > 0 && (
                <div
                  data-testid="quick-expense-desc-suggestions"
                  onMouseDown={(ev) => ev.stopPropagation()}
                  className="absolute left-0 right-0 top-full z-30 mt-1 max-h-48 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 shadow-xl"
                >
                  {suggestions.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      data-testid={`quick-expense-desc-suggestion-${s.label}`}
                      onMouseDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); setCe(s.label); setShowSug(false); }}
                      className="flex w-full items-center justify-between border-b border-zinc-900 px-2.5 py-1.5 text-left transition last:border-b-0 hover:bg-zinc-900/60"
                    >
                      <span className="truncate text-[11px] text-zinc-100">{s.label}</span>
                      <span className="ml-2 shrink-0 text-[10px] text-zinc-500">{s.count}×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number" step="0.01" value={suma}
                onChange={(e) => setSuma(e.target.value)}
                placeholder="Sumă (RON)"
                data-testid="quick-expense-suma"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500"
              />
              <input
                type="date" value={data}
                onChange={(e) => setData(e.target.value)}
                data-testid="quick-expense-data"
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500"
              />
            </div>
            <input
              type="text" value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notă (opțional)"
              data-testid="quick-expense-note"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500"
            />
            {err && <p className="text-[11px] text-red-400">{err}</p>}
            <div className="flex items-center justify-between pt-1 text-[10px] text-zinc-500">
              <span>Se salvează în Financiar → Cheltuieli</span>
              <button
                type="submit"
                disabled={!ce.trim() || sumaNum <= 0 || saving}
                data-testid="quick-expense-save"
                className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-orange-500 disabled:opacity-40"
              >
                {saving ? "..." : "Adaugă"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}



function NotificationBell({ variant = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: 5 } });
      setItems(r.data?.items || []);
      setUnread(Number(r.data?.unread || 0));
    } catch (_e) { /* polling is best-effort */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const markAll = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setUnread(0);
      setItems(items.map(x => ({ ...x, is_read: true })));
    } catch (_e) { /* ignore */ }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`notif-bell-wrap-${variant}`}>
      <button
        data-testid={`notif-bell-btn-${variant}`}
        onClick={() => setOpen(o => !o)}
        className="relative rounded-md p-2 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
        title={unread > 0 ? `${unread} notificări noi` : "Notificări"}
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-bell-badge" className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 py-0.5 text-[10px] font-bold text-white ring-2 ring-zinc-950">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notif-dropdown" className="absolute right-0 top-full z-50 mt-1.5 w-80 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
              <p className="text-xs font-semibold text-zinc-100">Notificări</p>
              <button
                data-testid="notif-mark-all-read"
                onClick={markAll}
                disabled={unread === 0}
                className="text-[10px] uppercase tracking-wider text-rose-300 hover:text-rose-200 disabled:opacity-40"
              >Marchează citite</button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 && (
                <div className="px-3 py-6 text-center text-xs text-zinc-500">
                  <Bell className="mx-auto mb-1 h-5 w-5 text-zinc-700" />
                  Nicio notificare nouă
                </div>
              )}
              {items.map((n) => (
                <div
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  className={`flex items-start gap-2 border-b border-zinc-900 px-3 py-2.5 ${!n.is_read ? "bg-rose-500/5" : ""}`}
                >
                  <span className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${!n.is_read ? "bg-rose-400" : "bg-transparent"}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[11px] ${!n.is_read ? "font-semibold text-zinc-100" : "text-zinc-400"}`}>{n.title}</p>
                    <p className="line-clamp-2 text-[10px] text-zinc-500">{n.message}</p>
                    {n.created_at && <p className="mt-0.5 text-[9px] text-zinc-600">{new Date(n.created_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}</p>}
                  </div>
                </div>
              ))}
              {items.length > 0 && (
                <div className="border-t border-zinc-800 bg-zinc-900/40 px-3 py-1.5 text-center text-[9px] text-zinc-600">
                  Maxim 5 notificări recente · cele mai vechi se înlocuiesc
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isOwner = user?.role === "owner";
  const allowed = user?.allowed_pages || [];
  const canAccess = (item) => isOwner || (item.page === undefined) || allowed.includes(item.page);
  // Filter groups + items down to what the current user can see.
  const GROUPS = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter(canAccess) })).filter((g) => g.items.length);
  const FLAT_NAV = GROUPS.flatMap((g) => g.items);
  const canSeeMessages = FLAT_NAV.some((n) => n.badgeKey === "messages");
  const canSeeMail = FLAT_NAV.some((n) => n.badgeKey === "mail");
  const canSeeWhatsApp = FLAT_NAV.some((n) => n.badgeKey === "whatsapp");
  const canSeeOrders = FLAT_NAV.some((n) => n.badgeKey === "orders");
  const unread = useUnreadMessages(canSeeMessages);
  const unreadMail = useUnreadMail(canSeeMail);
  const unreadWhatsApp = useUnreadWhatsApp(canSeeWhatsApp);
  const unreadMessenger = useUnreadMessenger(FLAT_NAV.some((n) => n.badgeKey === "messenger"));
  const newOrders = useNewOrders(canSeeOrders);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Desktop sidebar collapse — gives ~15rem more horizontal space, useful on
  // 13" MacBook screens where tables overflow (eMAG RMAs, Returns, etc.).
  // Persisted so the choice survives reloads.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("sidebar_collapsed", sidebarCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [sidebarCollapsed]);
  // Per-group open/closed state, persisted in localStorage
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nav_open_groups") || "null");
      if (saved) return saved;
    } catch (_e) { /* ignore */ }
    return Object.fromEntries(NAV_GROUPS.map((g) => [g.id, g.defaultOpen]));
  });
  useEffect(() => { try { localStorage.setItem("nav_open_groups", JSON.stringify(openGroups)); } catch (_e) { /* ignore */ } }, [openGroups]);

  // Auto-close the drawer whenever the route changes so navigating from the
  // drawer dismisses it without a manual click.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const currentLabel = (
    location.pathname === "/company" ? "Companie"
    : location.pathname === "/settings" ? "Setări"
    : location.pathname.startsWith("/team") ? "Echipa"
    : location.pathname.startsWith("/integrations") ? "Integrări"
    : (FLAT_NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "BragusApp")
  );

  const renderNavItem = ({ to, label, icon: Icon, testId, end, badgeKey }, testIdSuffix) => {
    const badge = badgeKey === "messages" && unread > 0 ? unread
      : badgeKey === "mail" && unreadMail > 0 ? unreadMail
      : badgeKey === "whatsapp" && unreadWhatsApp > 0 ? unreadWhatsApp
      : badgeKey === "messenger" && unreadMessenger > 0 ? unreadMessenger
      : badgeKey === "orders" && newOrders > 0 ? newOrders
      : null;
    return (
      <NavLink key={to} to={to} end={end} data-testid={`${testId}${testIdSuffix}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition ${
            isActive ? "bg-gradient-to-r from-orange-500/15 to-rose-500/5 text-orange-200 border border-orange-500/30" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border border-transparent"
          }`
        }>
        <Icon className="h-4 w-4" />
        <span className="flex-1">{label}</span>
        {badge !== null && (
          <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{badge}</span>
        )}
      </NavLink>
    );
  };

  const renderNavGroups = (testIdSuffix = "") => GROUPS.map((g) => {
    const isOpen = openGroups[g.id] !== false;
    return (
      <div key={g.id} className="mb-1">
        <button data-testid={`navgroup-${g.id}${testIdSuffix}`}
          onClick={() => setOpenGroups((s) => ({ ...s, [g.id]: !isOpen }))}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300">
          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {g.label}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5 pl-1">
            {g.items.map((it) => renderNavItem(it, testIdSuffix))}
          </div>
        )}
      </div>
    );
  });

  // Top-bar quick-access icons (next to bell): Integrări / Setări
  // Echipa, Companie și Coada de print au fost mutate în pagina Setări.
  const QuickIcons = ({ testIdSuffix = "" }) => (
    <div className="flex items-center gap-1">
      {isOwner && (
        <NavLink to="/integrations" data-testid={`quick-integrations${testIdSuffix}`} title="Integrări"
          className={({ isActive }) => `rounded-md p-2 transition ${isActive ? "text-orange-300 bg-orange-500/10" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"}`}>
          <Plug className="h-4 w-4" />
        </NavLink>
      )}
      <NavLink to="/settings" data-testid={`quick-settings${testIdSuffix}`} title="Setări"
        className={({ isActive }) => `rounded-md p-2 transition ${isActive ? "text-orange-300 bg-orange-500/10" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"}`}>
        <SettingsIcon className="h-4 w-4" />
      </NavLink>
    </div>
  );

  const Brand = ({ size = "md" }) => (
    <div className="flex items-center gap-3 px-2">
      <img src="/icons/logo-symbol-white.png" alt="Bragus" className={size === "lg" ? "h-9 w-auto" : "h-7 w-auto"} />
      <div>
        <h1 className={`font-bold tracking-tight text-zinc-50 ${size === "lg" ? "text-lg" : "text-base"}`}>Bragus App</h1>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Commerce Bridge</p>
      </div>
    </div>
  );

  const UserCard = () => user && (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-xs font-bold text-white">
          {(user.name || user.email || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="truncate text-xs font-medium text-zinc-100">{user.name || user.email.split("@")[0]}</p>
          <p className="truncate text-[10px] text-zinc-500">{user.role}</p>
        </div>
      </div>
      <button data-testid="logout-btn" onClick={logout}
        className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-[11px] text-zinc-300 hover:border-rose-500/40 hover:text-rose-300">
        <LogOut className="h-3 w-3" /> Logout
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-zinc-950 text-zinc-100">
      {/* Sidebar — desktop only, sticky to viewport. Collapses to a thin
          icon-only rail when `sidebarCollapsed` is true, giving wide tables
          (eMAG RMAs, Returns) the room they need on 13" MacBook screens. */}
      <aside data-testid="sidebar"
        className={`hidden flex-shrink-0 border-r border-zinc-800 bg-zinc-950/95 transition-all duration-200 md:flex md:flex-col md:sticky md:top-0 md:h-screen md:overflow-y-auto ${sidebarCollapsed ? "w-14 px-1 py-4" : "w-64 px-4 py-6"}`}>
        <div className={`mb-6 flex items-center ${sidebarCollapsed ? "justify-center" : "justify-between"}`}>
          {!sidebarCollapsed && <Brand size="lg" />}
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setSidebarCollapsed((v) => !v)}
            title={sidebarCollapsed ? "Extinde meniul" : "Ascunde meniul"}
            aria-label={sidebarCollapsed ? "Extinde meniul" : "Ascunde meniul"}
            className="rounded-md p-1.5 text-zinc-400 transition hover:bg-zinc-900 hover:text-zinc-200"
          >
            {sidebarCollapsed
              ? <PanelLeftOpen className="h-4 w-4" />
              : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        {sidebarCollapsed ? (
          <nav className="flex-1 flex flex-col items-center gap-1" data-testid="sidebar-nav-collapsed">
            {GROUPS.flatMap((g) => g.items).map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`${it.testId}-mini`}
                title={it.label}
                className={({ isActive }) => `flex h-9 w-9 items-center justify-center rounded-md transition ${isActive ? "bg-orange-500/15 text-orange-200" : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"}`}>
                <it.icon className="h-4 w-4" />
              </NavLink>
            ))}
          </nav>
        ) : (
          <>
            <nav className="flex-1 space-y-1" data-testid="sidebar-nav">{renderNavGroups()}</nav>
            <div className="mt-6"><UserCard /></div>
          </>
        )}
      </aside>

      {/* Mobile top bar — compact: hamburger | brand | bell. Safe-area aware. */}
      <header data-testid="mobile-topbar" className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between border-b border-zinc-800 bg-zinc-950/95 px-3 backdrop-blur md:hidden"
        style={{ paddingTop: "max(8px, env(safe-area-inset-top))", paddingBottom: 8 }}>
        <button data-testid="mobile-drawer-open" onClick={() => setDrawerOpen(true)}
          className="rounded-lg p-2 text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100" aria-label="Deschide meniu">
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/icons/logo-symbol-white.png" alt="Bragus" className="h-6 w-auto" />
          <span className="text-sm font-semibold tracking-tight">{currentLabel}</span>
        </div>
        <div className="flex items-center gap-0.5">
          <button
            data-testid="refresh-app-mobile"
            onClick={() => window.location.reload()}
            aria-label="Reîmprospătează"
            title="Reîmprospătează"
            className="rounded-lg p-2 text-zinc-300 transition active:rotate-180 active:scale-95 hover:bg-zinc-900 hover:text-zinc-100"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
          <QuickTaskButton variant="mobile" />
          <QuickExpenseButton variant="mobile" />
          <QuickIcons testIdSuffix="-mobile-top" />
          <NotificationBell variant="mobile" />
        </div>
      </header>

      {/* Mobile slide-in drawer with the full nav */}
      {drawerOpen && (
        <>
          <div data-testid="mobile-drawer-backdrop" onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden" />
          <aside data-testid="mobile-drawer" className="fixed left-0 top-0 z-50 flex h-full w-72 max-w-[85vw] flex-col border-r border-zinc-800 bg-zinc-950 px-4 py-5 shadow-2xl md:hidden"
            style={{ paddingTop: "max(20px, env(safe-area-inset-top))" }}>
            <div className="mb-6 flex items-center justify-between">
              <Brand size="md" />
              <button data-testid="mobile-drawer-close" onClick={() => setDrawerOpen(false)}
                className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100" aria-label="Închide meniu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto" data-testid="mobile-drawer-nav">{renderNavGroups("-mobile")}</nav>
            <div className="mt-4"><UserCard /></div>
          </aside>
        </>
      )}

      <div className="flex flex-1 flex-col min-w-0">
        {/* Desktop top bar — sticky to viewport so it stays during scroll. */}
        <header data-testid="desktop-topbar" className="sticky top-0 z-30 hidden items-center justify-between border-b border-zinc-800 bg-zinc-950/95 px-8 py-3 backdrop-blur md:flex">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Bragus App</p>
            <h2 className="text-lg font-semibold text-zinc-100">{currentLabel}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="refresh-app-desktop"
              onClick={() => window.location.reload()}
              aria-label="Reîmprospătează"
              title="Reîmprospătează aplicația"
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/60 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition hover:border-orange-500/40 hover:text-orange-200 active:rotate-180"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Refresh
            </button>
            <QuickTaskButton variant="desktop" />
            <QuickExpenseButton variant="desktop" />
            <span className="mx-2 h-5 w-px bg-zinc-800" />
            <QuickIcons testIdSuffix="-desktop" />
            <span className="mx-2 h-5 w-px bg-zinc-800" />
            <NotificationBell variant="desktop" />
          </div>
        </header>
        <main className="flex-1 overflow-x-auto pt-12 md:pt-0" data-testid="main-content"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
